/* ------------------------------------------------------------------
   Canette 3D (44 cl) dans les cartes biere : MuratDesigns, 26/09/2026.
   Module charge par snippets/gob-fiche-biere-autres.liquid.
   - Un rendu WebGL par carte, actif seulement quand la carte est visible.
   - Les textures lourdes (gouttes, relief, etiquette) sont generees UNE
     fois en canvas puis partagees entre les rendus.
   - Etiquette par produit : data-gob-etiquette (metachamp custom.etiquette_3d).
     Sans etiquette a plat, la canette porte Dark Reaper (data-gob-3d-etiquette).
   - Sans WebGL : rien ne change, la photo de la canette reste affichee.
------------------------------------------------------------------ */
import * as THREE from 'https://cdn.jsdelivr.net/npm/three@0.169.0/build/three.module.min.js';

// plusieurs zones possibles par page (hero + grille de la collection, 26/09/2026)
const sections = [...document.querySelectorAll('[data-gob-3d-etiquette]')];
const section = sections[0];
const cartes = sections.flatMap(s => [...s.querySelectorAll('.gob-carte__haut[data-gob-etiquette], [data-gob-3d-slot]')]);
const reduit = matchMedia('(prefers-reduced-motion: reduce)').matches;

// demarrage paresseux : rien ne se passe tant que la section n'approche pas
// de l'ecran, puis on attend un moment libre du navigateur (26/09/2026)
// connexion lente ou economie de donnees : pas de 3D, les photos restent
window.__gob3d = true;
const cnx = navigator.connection;
const lent = !!(cnx && (cnx.saveData || /(^|-)2g|3g/.test(cnx.effectiveType || '')));
function sans3d() { document.documentElement.classList.remove('gob-3d-ok'); }
if (!cartes.length || lent || !webglOk()) sans3d();
if (cartes.length && webglOk() && !lent) {
  const io = new IntersectionObserver(e => {
    if (!e[0].isIntersecting) return;
    io.disconnect();
    (window.requestIdleCallback || (f => setTimeout(f, 60)))(() => demarrer(), { timeout: 1200 });
  }, { rootMargin: '900px 0px' });
  sections.forEach(s => io.observe(s));
}

function webglOk() {
  try { const c = document.createElement('canvas'); return !!(c.getContext('webgl2') || c.getContext('webgl')); }
  catch (e) { return false; }
}

/* ---------- dimensions 44 cl : 66 x 157 mm ---------- */
const R = 0.033, H = 0.157, Y0 = -H / 2, YT = Y0 + H;
const Y_BAS = Y0 + 0.0078, Y_HAUT = YT - 0.0120;
const SEG = 128;
const CIRC = 2 * Math.PI * R, HCORPS = Y_HAUT - Y_BAS;
const TX = 1024, TY = Math.round(TX * HCORPS / CIRC);

/* ---------- textures partagees (canvas) ---------- */
function gouttesListe() {
  const rnd = (a, b) => a + Math.random() * (b - a), l = [];
  for (let i = 0; i < 1500; i++) {
    const t = Math.random();
    l.push({ x: rnd(0, TX), y: rnd(0, TY), r: t < .7 ? rnd(1.3, 2.3) : t < .96 ? rnd(2.3, 4) : rnd(4, 6), e: rnd(1.05, 1.3) });
  }
  return l;
}
function dessine(liste, g, echelle, fn) {
  for (const d of liste) for (const dx of [-TX, 0, TX]) {
    if (d.x + dx < -10 || d.x + dx > TX + 10) continue;
    fn(g, (d.x + dx) * echelle, d.y * echelle, d.r * echelle, d.e);
  }
}
function canvasNormales(liste) {
  const c = document.createElement('canvas'); c.width = TX; c.height = TY;
  const g = c.getContext('2d', { willReadFrequently: true });
  g.fillStyle = '#000'; g.fillRect(0, 0, TX, TY);
  g.globalCompositeOperation = 'lighten';
  dessine(liste, g, 1, (g, x, y, r, e) => {
    const gr = g.createRadialGradient(x, y, 0, x, y, r);
    gr.addColorStop(0, '#fff'); gr.addColorStop(.5, '#dedede'); gr.addColorStop(.75, '#a8a8a8');
    gr.addColorStop(.92, '#5a5a5a'); gr.addColorStop(1, '#000');
    g.fillStyle = gr; g.save(); g.translate(x, y); g.scale(1, e); g.translate(-x, -y);
    g.beginPath(); g.arc(x, y, r, 0, Math.PI * 2); g.fill(); g.restore();
  });
  const h = g.getImageData(0, 0, TX, TY).data, out = g.createImageData(TX, TY);
  const H_ = (x, y) => h[((Math.min(Math.max(y, 0), TY - 1)) * TX + ((x + TX) % TX)) * 4] / 255;
  for (let y = 0; y < TY; y++) for (let x = 0; x < TX; x++) {
    const dx = (H_(x + 1, y) - H_(x - 1, y)) * 2.2, dy = (H_(x, y + 1) - H_(x, y - 1)) * 2.2;
    const l = Math.hypot(dx, dy, 1), k = (y * TX + x) * 4;
    out.data[k] = (-dx / l * .5 + .5) * 255; out.data[k + 1] = (dy / l * .5 + .5) * 255;
    out.data[k + 2] = (1 / l * .5 + .5) * 255; out.data[k + 3] = 255;
  }
  g.globalCompositeOperation = 'source-over'; g.putImageData(out, 0, 0);
  return c;
}
function ombrer(liste, g, echelle) {
  dessine(liste, g, echelle, (g, x, y, r, e) => {
    g.save(); g.translate(x, y); g.scale(1, e);
    const gr = g.createRadialGradient(0, r * .15, r * .2, 0, 0, r);
    gr.addColorStop(0, 'rgba(0,0,0,0)'); gr.addColorStop(.75, 'rgba(0,0,0,.08)'); gr.addColorStop(1, 'rgba(0,0,0,.24)');
    g.fillStyle = gr; g.beginPath(); g.arc(0, 0, r, 0, Math.PI * 2); g.fill();
    g.fillStyle = 'rgba(255,255,255,.10)';
    g.beginPath(); g.ellipse(0, r * .45, r * .55, r * .28, 0, 0, Math.PI * 2); g.fill();
    g.fillStyle = 'rgba(255,255,255,.2)';
    g.beginPath(); g.ellipse(-r * .28, -r * .36, r * .16, r * .1, -.5, 0, Math.PI * 2); g.fill();
    g.restore();
  });
}
function charger(src) {
  return new Promise((ok, ko) => { const i = new Image(); i.crossOrigin = 'anonymous'; i.onload = () => ok(i); i.onerror = ko; i.src = src; });
}

async function demarrer() {
  let img;
  try { img = await charger(section.getAttribute('data-gob-3d-etiquette')); } catch (e) { sans3d(); return; }
  const liste = gouttesListe();
  const cNorm = canvasNormales(liste);
  // etiquette a plat -> canvas avec les gouttes peintes (une par etiquette differente)
  const versCanvas = im => {
    const cv = document.createElement('canvas'); cv.width = 1200; cv.height = Math.round(1200 * TY / TX);
    const g = cv.getContext('2d'), m = Math.round(im.naturalHeight * .012);
    g.drawImage(im, m, m, im.naturalWidth - 2 * m, im.naturalHeight - 2 * m, 0, 0, cv.width, cv.height);
    ombrer(liste, g, cv.width / TX); return cv;
  };
  const cEtiq = versCanvas(img);
  const cNoir = document.createElement('canvas'); cNoir.width = TX / 2; cNoir.height = TY / 2;
  { const g = cNoir.getContext('2d'); g.fillStyle = '#161618'; g.fillRect(0, 0, cNoir.width, cNoir.height); ombrer(liste, g, .5); }
  let moteur;
  try { moteur = creerMoteur({ cNorm, cEtiq, cNoir }); } catch (e) { sans3d(); return; }
  // fond : on garde le fond flou de chaque biere (couleur de sa canette)
  const vues = cartes.map((haut, i) => vue(haut, i));
  // etiquette propre a un produit (data-gob-etiquette, metachamp custom.etiquette_3d) :
  // chargee en arriere-plan, la canette garde l'etiquette par defaut en attendant (26/09/2026)
  const cache = {};
  vues.forEach(v => {
    const url = v.haut.getAttribute('data-gob-etiquette');
    if (!url) return;
    cache[url] = cache[url] || charger(url).then(im => moteur.texEtiq(versCanvas(im)));
    cache[url].then(t => { v.tex = t; v.sig = null; }, () => {});
  });
  boucle(moteur, vues);
}

/* ---------- UN seul moteur WebGL pour toutes les cartes ----------
   Chaque carte a un simple canvas 2D : on rend la scene pour elle dans le
   moteur (hors DOM), puis on recopie l'image. Shaders, eclairage et
   textures ne sont prepares qu'une fois, quel que soit le nombre de cartes. */
function creerMoteur(c) {
  const canvas = document.createElement('canvas');
  const renderer = new THREE.WebGLRenderer({ canvas, antialias: true, alpha: true, powerPreference: 'low-power' });
  renderer.setPixelRatio(1);
  renderer.toneMapping = THREE.ACESFilmicToneMapping;
  renderer.toneMappingExposure = 1.3;
  renderer.outputColorSpace = THREE.SRGBColorSpace;
  renderer.setClearColor(0x000000, 0);

  const scene = new THREE.Scene();
  const pmrem = new THREE.PMREMGenerator(renderer);
  {
    const st = new THREE.Scene(); st.background = new THREE.Color(0x050505);
    const panneau = (w, h, x, y, z, k) => {
      const m = new THREE.Mesh(new THREE.PlaneGeometry(w, h), new THREE.MeshBasicMaterial({ color: new THREE.Color(k, k, k), side: THREE.DoubleSide }));
      m.position.set(x, y, z); m.lookAt(0, y, 0); st.add(m);
    };
    panneau(.5, 6, -3.2, 0, 1.6, 9); panneau(.25, 6, 3.0, 0, 1.2, 5);
    panneau(1.6, 4, 0.8, 0, 3.5, 1.4); panneau(.15, 6, -1.2, 0, -3.2, 3);
    const hautL = new THREE.Mesh(new THREE.PlaneGeometry(4, 4), new THREE.MeshBasicMaterial({ color: new THREE.Color(2, 2, 2) }));
    hautL.position.set(0, 4, 0); hautL.rotation.x = Math.PI / 2; st.add(hautL);
    scene.environment = pmrem.fromScene(st, 0.02).texture;
    pmrem.dispose();
  }
  const cle = new THREE.DirectionalLight(0xffffff, 1.2); cle.position.set(-.4, .6, .5); scene.add(cle);

  const tex = (cv, srgb) => { const t = new THREE.CanvasTexture(cv); if (srgb) t.colorSpace = THREE.SRGBColorSpace; t.wrapS = t.wrapT = THREE.RepeatWrapping; return t; };
  const tEtiq = tex(c.cEtiq, true), tNorm = tex(c.cNorm), tNoir = tex(c.cNoir, true);
  tEtiq.anisotropy = tNorm.anisotropy = Math.min(8, renderer.capabilities.getMaxAnisotropy());
  tEtiq.offset.x = tNorm.offset.x = 0.63;
  const tNormN = tNorm.clone(); tNormN.repeat.set(1, .35); tNormN.offset.x = 0; tNoir.repeat.set(1, .35);

  const matCorps = new THREE.MeshPhysicalMaterial({ map: tEtiq, metalness: .2, roughness: .32,
    normalMap: tNorm, normalScale: new THREE.Vector2(.28, .28), clearcoat: 1, clearcoatRoughness: .1,
    clearcoatNormalMap: tNorm, clearcoatNormalScale: new THREE.Vector2(.55, .55) });
  const matNoir = new THREE.MeshPhysicalMaterial({ map: tNoir, metalness: .45, roughness: .28,
    normalMap: tNormN, normalScale: new THREE.Vector2(.28, .28), clearcoat: 1, clearcoatRoughness: .1,
    clearcoatNormalMap: tNormN, clearcoatNormalScale: new THREE.Vector2(.55, .55), side: THREE.DoubleSide });
  const matFond = matNoir.clone(); matFond.normalScale.setScalar(.15); matFond.clearcoatNormalScale.setScalar(.25);
  const matAlu = new THREE.MeshPhysicalMaterial({ color: 0xc4c7cc, metalness: 1, roughness: .2, side: THREE.DoubleSide });

  const P = (r, y) => new THREE.Vector2(r, y);
  const corps = new THREE.Mesh(new THREE.CylinderGeometry(R, R, HCORPS, SEG, 1, true), matCorps);
  corps.position.y = (Y_HAUT + Y_BAS) / 2;
  const bas = new THREE.Mesh(new THREE.LatheGeometry([
    P(0, Y0 + .0095), P(.012, Y0 + .0085), P(.019, Y0 + .006), P(.0228, Y0 + .0025), P(.0238, Y0 + .0006),
    P(.0248, Y0), P(.0256, Y0 + .0004), P(.0259, Y0 + .001), P(.0312, Y0 + .0057), P(.03124, Y0 + .00575),
    P(.0323, Y0 + .0064), P(.03236, Y0 + .00645), P(.0329, Y0 + .0071), P(R, Y0 + .0077), P(R, Y_BAS)], SEG), matFond);
  const epaule = new THREE.Mesh(new THREE.LatheGeometry([
    P(R, Y_HAUT), P(.0329, YT - .011), P(.0326, YT - .0104), P(.03256, YT - .01035), P(.0301, YT - .0068),
    P(.0276, YT - .0034), P(.02755, YT - .00335), P(.0268, YT - .0027), P(.0263, YT - .0022),
    P(.0259, YT - .0017), P(.0261, YT - .0009), P(.0259, YT - .0002), P(.0254, YT), P(.0249, YT - .0003), P(.0246, YT - .0012)], SEG), matNoir);
  const couvercle = new THREE.Mesh(new THREE.LatheGeometry([
    P(.0246, YT - .0012), P(.0244, YT - .0048), P(.0239, YT - .0057), P(.0233, YT - .0054),
    P(.0227, YT - .0049), P(.018, YT - .0047), P(0, YT - .0046)], SEG), matAlu);
  const forme = new THREE.Shape(), tl = .0105, tw = .0085;
  forme.absarc(0, 0, tw / 2, Math.PI, 0, true); forme.lineTo(tw / 2, tl); forme.absarc(0, tl, tw / 2, 0, Math.PI, true); forme.lineTo(-tw / 2, 0);
  const trou = new THREE.Path(); trou.absellipse(0, .001, .0028, .0022, 0, Math.PI * 2, false); forme.holes.push(trou);
  const languette = new THREE.Mesh(new THREE.ExtrudeGeometry(forme, { depth: .0005, bevelEnabled: true, bevelThickness: .0002, bevelSize: .0003, bevelSegments: 2 }),
    new THREE.MeshPhysicalMaterial({ color: 0xb0b4ba, metalness: 1, roughness: .25 }));
  languette.rotation.x = -Math.PI / 2; languette.position.set(0, YT - .0044, -.004);

  const canette = new THREE.Group(); canette.add(corps, bas, epaule, couvercle, languette);
  const pivot = new THREE.Group(); pivot.add(canette); scene.add(pivot);
  // ombre : halo flou en CSS sous la canette (statique, donc gratuit au defilement)
  const camera = new THREE.PerspectiveCamera(22, 3 / 4, .01, 10);
  camera.position.set(0, .006, .56); camera.lookAt(0, .006, 0);
  renderer.compile(scene, camera);          // shaders prepares une seule fois, tout de suite
  const texEtiq = cv => { const t = tex(cv, true); t.anisotropy = tEtiq.anisotropy; t.offset.x = .63; return t; };
  return { renderer, scene, camera, canette, pivot, matCorps, tEtiq, texEtiq };
}

/* ---------- etat d'une carte ---------- */
function vue(haut, idx) {
  const carte = haut.closest('.gob-carte') || haut;
  const cv = document.createElement('canvas');
  cv.className = 'gob-carte__3d'; cv.setAttribute('aria-hidden', 'true');
  haut.appendChild(cv);
  const v = { haut, idx, cv, ctx: cv.getContext('2d'), visible: false, pret: false,
    survol: false, sx: 0, sy: 0, cx: 0, cy: 0, slot: haut.hasAttribute('data-gob-3d-slot'),
    repos: haut.dataset.penche !== undefined ? +haut.dataset.penche : (haut.closest('[data-gob-3d-droit]') ? 0 : -.1), angle: idx * 1.4 + (+haut.dataset.angle || 0), vit: .22, penche: 0, leve: 0 };
  v.penche = v.repos;
  // canettes du hero (data-gob-3d-slot) : decor, aucune interaction
  if (!haut.hasAttribute('data-gob-3d-slot')) {
  carte.addEventListener('pointerenter', () => v.survol = true);
  carte.addEventListener('pointerleave', () => { v.survol = false; v.sx = v.sy = 0; });
  carte.addEventListener('pointermove', e => {
    const r = haut.getBoundingClientRect();
    v.sx = (e.clientX - r.left) / r.width - .5; v.sy = (e.clientY - r.top) / r.height - .5;
  });
  }
  new IntersectionObserver(e => { v.visible = e[0].isIntersecting; }, { rootMargin: '80px' }).observe(haut);
  return v;
}

/* ---------- une boucle pour toutes les cartes ---------- */
function boucle(m, vues) {
  const dprMax = devicePixelRatio || 1;
  let t0 = performance.now(), lw = 0, lh = 0;
  function image(t) {
    requestAnimationFrame(image);
    if (t - t0 < 30) return;                         // ~30 images/s : fluide, deux fois moins de calcul
    const dt = Math.min((t - t0) / 1000, .08); t0 = t;
    for (const v of vues) {
      if (!v.visible) continue;
      const dpr = Math.min(dprMax, v.slot ? 1.35 : 1.2);
      const w = Math.round(v.cv.clientWidth * dpr), h = Math.round(v.cv.clientHeight * dpr);
      if (!w || !h) continue;
      if (v.cv.width !== w || v.cv.height !== h) { v.cv.width = w; v.cv.height = h; }
      if (w > lw || h > lh) { lw = Math.max(lw, w); lh = Math.max(lh, h); m.renderer.setSize(lw, lh, false); m.renderer.setScissorTest(true); }
      m.renderer.setViewport(0, 0, w, h); m.renderer.setScissor(0, 0, w, h);
      if (m.camera.aspect !== w / h) { m.camera.aspect = w / h; m.camera.updateProjectionMatrix(); }
      // rotation lente en continu, plus rapide au survol de la carte
      v.vit += ((v.survol ? 1.6 : .22) - v.vit) * .05;
      if (!reduit) v.angle += dt * v.vit;
      v.penche += ((v.survol ? 0 : v.repos) - v.penche) * .08;
      v.leve += ((v.survol ? .006 : 0) - v.leve) * .08;
      v.cx += (v.sx - v.cx) * .08; v.cy += (v.sy - v.cy) * .08;
      const sig = [v.angle, v.penche, v.leve, v.cx, v.cy, w, h].map(n => n.toFixed(4)).join();
      if (sig === v.sig && v.pret) continue;
      v.sig = sig;
      m.canette.rotation.y = v.angle;
      m.pivot.rotation.z = v.penche + v.cx * .12;
      m.pivot.rotation.x = .1 + v.cy * .14;
      m.pivot.rotation.x = v.slot ? .08 : m.pivot.rotation.x;
      m.pivot.position.y = v.leve;
      const tx = v.tex || m.tEtiq;
      if (m.matCorps.map !== tx) m.matCorps.map = tx;
      m.renderer.render(m.scene, m.camera);
      v.ctx.clearRect(0, 0, w, h);
      v.ctx.drawImage(m.renderer.domElement, 0, lh - h, w, h, 0, 0, w, h);   // WebGL : origine en bas a gauche
      if (!v.pret) { v.pret = true; v.haut.classList.add('is-3d'); }
    }
  }
  requestAnimationFrame(image);
}
